import { post as amplifyPost, put as amplifyPut, get as amplifyGet, del as amplifyDel, patch as amplifyPatch } from "aws-amplify/api";
import config from "../config";
import { ConsoleLogger } from 'aws-amplify/utils';

const logger = new ConsoleLogger("api-handler");
const API_NAME = config.API_NAME;

/**
 * Handles the response from an Amplify API call, parsing the JSON body
 * and logging any errors that occur.
 * @param {Promise} responsePromise - The promise returned from an Amplify API call.
 * @param {string} path - The API path that was called, for logging purposes.
 * @returns {Promise<any>} The parsed JSON body of the response.
 * @throws Will re-throw any errors that occur during the API call or JSON parsing.
 */
async function handleResponse(responsePromise, path, ignoreErrors = false) {
    try {
        const response = await responsePromise;
        const { statusCode, body } = response;

        if (statusCode >= 200 && statusCode < 300) { // Successful response
            try {
                // The response body is a stream. We try to read it as JSON.
                return await body.json();
            } catch (e) {
                // This error is expected if the body is empty (e.g., for a 204 No Content response).
                // We can log it for debugging but return null to indicate success without data.
                logger.info(`API call to path "${path}" was successful but had no JSON body.`);
                return false;
            }
        } 
        else { // Error response
            if(!ignoreErrors){
                const errorBody = await body.text();
                logger.error(`API call to ${path} failed with status code ${statusCode}: ${errorBody}`);
                throw new Error(`API call to ${path} failed with status code ${statusCode}. Body: ${errorBody}`);
            }
        }
    } catch (error) {
        if(!ignoreErrors){
            logger.error(`API call to path "${path}" failed:`, error);
            throw error; // Re-throw the error to be handled by the calling function
        }
    }
}

/**
 * Performs a GET request to the application's API.
 * @param {string} path - The API endpoint path.
 * @param {object} [options={}] - Optional Amplify API options.
 * @returns {Promise<any>} The JSON response from the API.
 */
export const get = async (path, options = {}) => {
    return await handleResponse(await amplifyGet({ apiName: API_NAME, path, options }).response, path);
};

/**
 * Performs a POST request to the application's API.
 * @param {string} path - The API endpoint path.
 * @param {object} body - The request body.
 * @param {object} [options={}] - Optional Amplify API options.
 * @returns {Promise<any>} The JSON response from the API.
 */
export const post = async (path, body, options = {}, ignoreErrors) => {
    const finalOptions = { ...options, body };
    return await handleResponse(amplifyPost({ apiName: API_NAME, path, options: finalOptions }).response, path, ignoreErrors);
};

/**
 * Performs a PUT request to the application's API.
 * @param {string} path - The API endpoint path.
 * @param {object} body - The request body.
 * @param {object} [options={}] - Optional Amplify API options.
 * @returns {Promise<any>} The JSON response from the API.
 */
export const put = async (path, body, options = {}) => {
    const finalOptions = { ...options, body };
    return await handleResponse(amplifyPut({ apiName: API_NAME, path, options: finalOptions }).response, path);
};

/**
 * Performs a DELETE request to the application's API.
 * @param {string} path - The API endpoint path.
 * @param {object} [options={}] - Optional Amplify API options, can include a body.
 * @returns {Promise<any>} The JSON response from the API.
 */
export const del = async (path, options = {}) => {
    return await handleResponse(amplifyDel({ apiName: API_NAME, path, options }).response, path);
};

/**
 * Performs a PATCH request to the application's API.
 * Note: Amplify's API does not have a dedicated `patch` method, so we use `post`
 * and specify the method in the headers.
 * @param {string} path - The API endpoint path.
 * @param {object} body - The request body.
 * @param {object} [options={}] - Optional Amplify API options.
 * @returns {Promise<any>} The JSON response from the API.
 */
export const patch = async (path, body, options = {}) => {
  const finalOptions = { ...options, body };
  return await handleResponse(amplifyPatch({ apiName: API_NAME, path, options: finalOptions }).response, path);
};